import type { QueueEntry } from "../../api/types";
import { formatElapsed, pluralize } from "../../lib/format";
import type { WaitingRow, WaitingUnit } from "../../lib/waiting";
import { ordinal, rowsFromQueue } from "../../lib/waiting";

interface WaitingListProps {
  rows: WaitingRow[];
  unit: WaitingUnit;
  /** The signed-in researcher: their own row reads "you". */
  you: string;
}

/**
 * The line itself: one row per request, in the order they will be served.
 *
 * Every row says when its request was **asked for**, never how long it has
 * waited: a resize puts a workspace back in the queue without changing when it
 * was asked for, so the two are not the same number and only one of them is
 * published. The clause on the right is the console's own, rendered as it
 * stands.
 *
 * Args:
 *     rows: The queue, in service order.
 *     unit: What this lane is counted in.
 *     you: The signed-in researcher's username.
 *
 * Returns:
 *     The list, or null when nobody is waiting.
 */
export function WaitingList({
  rows,
  unit,
  you,
}: WaitingListProps): JSX.Element | null {
  if (rows.length === 0) {
    return null;
  }
  return (
    <ul className="waiting-list">
      {rows.map((row) => (
        <li
          key={row.key}
          className="waiting-row"
          data-mine={row.user === you ? "true" : undefined}
        >
          <span className="waiting-row__pos">{ordinal(row.position)}</span>
          <span className="waiting-row__who">
            {row.user === you ? "you" : row.user}
          </span>
          <span className="waiting-row__size">
            {pluralize(row.gpus ?? row.cores ?? 0, unit)}
          </span>
          <span className="waiting-row__asked">
            asked {formatElapsed(row.asked_at)} ago
          </span>
          <span className="waiting-row__note">{row.note}</span>
        </li>
      ))}
    </ul>
  );
}

interface WaitingSectionProps {
  rows: QueueEntry[];
  unit: WaitingUnit;
  you: string;
}

/**
 * Who is waiting for this fleet, on the screen that shows the fleet.
 *
 * It sits between the workspaces somebody holds and the machines that are
 * running, because a researcher reads *where do I stand* before *what is
 * running* — and because a queue is a fact about the fleet, not about them.
 *
 * Nothing at all is rendered when nobody is waiting: an empty container with a
 * heading would say "the queue is here" on the ordinary day when there is no
 * queue.
 *
 * Args:
 *     rows: `capacity.queue`, or `capacity.cpu.queue`.
 *     unit: What this lane is counted in.
 *     you: The signed-in researcher's username.
 *
 * Returns:
 *     The section, or null when nobody is waiting.
 */
export function WaitingSection({
  rows,
  unit,
  you,
}: WaitingSectionProps): JSX.Element | null {
  if (rows.length === 0) {
    return null;
  }
  return (
    <section className="section" aria-labelledby="waiting-title">
      <div className="section__head">
        <h2 className="section__title" id="waiting-title">
          Waiting
        </h2>
        {/*
          A count, and no rule: the lane is not purely oldest-first — a
          workspace is served before every job, whatever their ages — and the
          rule that does apply to one request is written by the server, in that
          request's own sentence and in the clause on its row. A heading
          restating it here would be a second author, and a wrong one whenever
          an older job stands behind a newer workspace.
        */}
        <span className="section__meta">
          {pluralize(rows.length, "request")} in line
        </span>
      </div>
      <WaitingList rows={rowsFromQueue(rows)} unit={unit} you={you} />
    </section>
  );
}
